/* eslint-disable react/prop-types */
import { Box, Divider, Typography } from '@mui/material';
import dayjs from 'dayjs';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AssetTable from './AssetTable';
import Header from './Header';
import KPIsGraphs from './KPIsGraphs';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface MaintenanceDashboardSectionProps {
  assetsItems: any[];
  filteredAssets: any[];
  selectedMonth: string;
  isUploadWorkOrder: boolean;
  setShowView: (view: string | null) => void;
  handleIsWorkOrder: (check: boolean) => void;
  handleMonthSelect: (selectedMonth: string | null) => void;
  showDetails: (asset: any) => void;
  setShowAssetDetails: (param: boolean) => void;
}

interface KpisAndGraphsSectionProps {
  assetsItems: any[];
  setShowView: (view: string | null) => void;
}

interface AssetsViewSectionProps {
  assetsItems: any[];
  filteredAssets: any[];
  selectedMonth: string;
  isUploadWorkOrder: boolean;
  handleIsWorkOrder: (check: boolean) => void;
  handleMonthSelect: (selectedMonth: string | null) => void;
  showDetails: (asset: any) => void;
  setShowAssetDetails: (param: boolean) => void;
}

const KpisAndGraphsSection: React.FC<KpisAndGraphsSectionProps> = ({
  assetsItems,
  setShowView,
}) => {
  const { t } = useTranslation();
  const squaresHandler = new SquaresDashboardHandler();
  const dispatch = useAppDispatch();

  // seelctors
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);

  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );

  let initialStartDate = new Date();
  initialStartDate.setFullYear(initialStartDate.getFullYear() - 1);
  let initialEndDate = new Date();
  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);
    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }

  const [selectedStartDate, setStartDate] = useState(initialStartDate);
  const [selectedEndDate, setEndDate] = useState(initialEndDate);
  const [isLoading, setIsLoading] = useState(false);

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;

    setStartDate(startDate);
    setEndDate(endDate);

    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };

  const fetchData = async () => {
    try {
      if (selectedStartDate && selectedEndDate) {
        setIsLoading(true);
        await Promise.all([
          squaresHandler.getDashboardAssetsData(
            selectedStartDate,
            selectedEndDate
          ),
          squaresHandler.getPmVsCmVsModification(
            selectedStartDate,
            selectedEndDate
          ),
          squaresHandler.getWorkOrdersWithDowntimeAggregated(
            selectedStartDate,
            selectedEndDate
          ),
        ]);
      } else {
        dispatch(
          showNotification({
            message: t('please_select_a_valid_date_range'),
            type: 'error',
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      void fetchData();
    }
  }, [accessToken]);

  return (
    <Box width={'20%'}>
      <Box display="flex" flexDirection="column" gap={0} marginBottom={1}>
        <Typography
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('date_range')}{' '}
        </Typography>
        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          alignContent={'center'}
          // height={'30px'}
          gap={1}
          flexWrap={'wrap'}
        >
          <CustomDateRangePicker
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            maxDate={dayjs()}
            wrap
            showDash={false}
            onFinalDateChange={handleDateChange}
            disabled={isLoading}
          />
          {/* <Button
          
         
          >
            Apply
          </Button> */}
          <Box width={'50px'}>
            <CustomLoadingButton
              onClick={fetchData}
              loading={isLoading}
              sx={{
                fontSize: '12px',
                height: '30px',
              }}
              fullWidth
              disabled={isLoading}
            >
              {t('apply')}
            </CustomLoadingButton>
          </Box>
        </Box>
      </Box>
      <KPIsGraphs
        assetsItems={assetsItems}
        setShowView={setShowView}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
      />
    </Box>
  );
};

const AssetsViewSection: React.FC<AssetsViewSectionProps> = ({
  assetsItems,
  filteredAssets,
  handleIsWorkOrder,
  handleMonthSelect,
  isUploadWorkOrder,
  selectedMonth,
  showDetails,
  setShowAssetDetails,
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <Box width={'75%'}>
      <Header
        allAssets={assetsItems}
        handleIsWorkOrder={handleIsWorkOrder}
        isUploadWorkOrder={isUploadWorkOrder}
        onMonthSelect={handleMonthSelect}
      />
      <AssetTable
        assets={assetsItems}
        filteredAssets={filteredAssets}
        enableDarkTheme={enableDarkTheme}
        onShowDetails={showDetails}
        selectedMonth={selectedMonth}
        setShowAssetDetails={setShowAssetDetails}
      />
    </Box>
  );
};
export const MaintenanceDashboardSection: React.FC<
  MaintenanceDashboardSectionProps
> = ({
  assetsItems,
  filteredAssets,
  selectedMonth,
  isUploadWorkOrder,
  setShowView,
  handleIsWorkOrder,
  handleMonthSelect,
  showDetails,
  setShowAssetDetails,
}) => {
  return (
    <Box display={'flex'} flexDirection={'row'} gap={2} marginTop={2}>
      <KpisAndGraphsSection
        assetsItems={assetsItems}
        setShowView={setShowView}
      />
      <Divider orientation="vertical" flexItem sx={{ marginX: 2 }} />

      <AssetsViewSection
        assetsItems={assetsItems}
        filteredAssets={filteredAssets}
        selectedMonth={selectedMonth}
        isUploadWorkOrder={isUploadWorkOrder}
        showDetails={showDetails}
        handleIsWorkOrder={handleIsWorkOrder}
        handleMonthSelect={handleMonthSelect}
        setShowAssetDetails={setShowAssetDetails}
      />
    </Box>
  );
};
